/**
 * intakeAnswers — pure transformation from raw intake_field_data rows into
 * the normalised IntakeAnswers contract the conditional document-checklist
 * engine (documentChecklist.ts) consumes.
 *
 * Extracted out of Documents.tsx (2026-07-13) so it's unit-testable and to
 * fix a dead-code bug: intake_field_data stores one row per TOP-LEVEL
 * canonical segment ('applicant' / 'sponsor' / 'application') holding the
 * WHOLE nested object (autosave), plus occasional flat dotted seed rows
 * (agent prefill). The previous implementation did literal
 * `map.get('applicant.relationship.status')`-style lookups on the dotted
 * canonical name, which never matches that blob shape — every signal below
 * was permanently undefined in production. The fix hydrates each row set via
 * expandFormState(rowsToFormState(rows)) — the same path the intake form
 * itself uses to reconstruct nested state — then reads by nested access.
 *
 * TWO SEPARATE ROW SETS: per the schema field-ownership registry
 * (src/schema/shared-fields-registry.json), applicant-owned canonical fields
 * (applicant.relationship.status, applicant.previous_relationships.*, etc.)
 * are declared with owner_form on an applicant form (47SP_*) and so live in
 * the APPLICANT's own intake_form's rows. Sponsor-owned fields
 * (sponsor.previous_relationships.*) are declared with owner_form
 * "40SP_sponsor" and so live in the SPONSOR's own intake_form's rows — a
 * different form_id, fetched separately. Passing only the applicant's rows
 * therefore leaves every `sponsor*` signal undefined by design (matches
 * today's default when a matter has no sponsor form yet); pass the sponsor's
 * rows too once resolved to populate them.
 *
 * Since Jane's reorg (2026-07-16) the partner applicant forms carry four
 * portal-native "Relationship evidence" tabs (applicant.evidence.financial /
 * household / social / commitment) whose structured answers feed the
 * joint-finance, household, social and commitment signals below, so the
 * matching evidence documents flip to required per situation. On matters where
 * those tabs are untouched the signals stay undefined and the docs render as
 * optional, exactly as before. Any unknown key resolves to undefined (the safe
 * default).
 */

import { expandFormState, rowsToFormState, readNestedPath } from './schema-runtime/formStateAdapter';
import type { IntakeAnswers } from './documentChecklist';

/** One row as returned by `select('field_key, field_value')` on intake_field_data. */
export interface IntakeFieldRow {
  field_key: string;
  field_value: unknown;
}

/**
 * Map raw intake_field_data rows into the normalised IntakeAnswers contract.
 *
 * `applicantRows` — every intake_field_data row for the matter's applicant
 * intake_form (required source for all applicant.* signals).
 * `sponsorRows` — every intake_field_data row for the matter's sponsor
 * intake_form, when one exists (required source for all sponsor.* signals;
 * defaults to `[]`, which resolves every sponsor.* signal to undefined —
 * the same safe default as when no sponsor form exists yet).
 */
export function intakeAnswersFromFieldData(
  applicantRows: IntakeFieldRow[],
  sponsorRows: IntakeFieldRow[] = [],
): IntakeAnswers {
  const applicant = expandFormState(rowsToFormState(applicantRows));
  const sponsor = expandFormState(rowsToFormState(sponsorRows));

  const out: IntakeAnswers = {};

  const status = readNestedPath(applicant, 'applicant.relationship.status');
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    if (s.includes('married')) out.relationshipStatus = 'married';
    else if (s.includes('de facto') || s.includes('defacto') || s.includes('de-facto')) out.relationshipStatus = 'defacto';
    else if (s.includes('engaged')) out.relationshipStatus = 'engaged';
  }

  const registeredState = readNestedPath(applicant, 'applicant.relationship.registered_state');
  if (typeof registeredState === 'string' && registeredState.trim() !== '') {
    out.registeredState = registeredState.trim();
  }

  out.livedSeparately = isYesValue(readNestedPath(applicant, 'applicant.relationship.lived_separately'));
  // ImmiAccount's visa-history gate conflates "travelled to Australia" with
  // "previously applied for a visa". Treating Yes as "has entered" is the
  // conservative direction (shows the optional AU police check rather than
  // hiding it for offshore applicants).
  out.applicantEverEnteredAustralia = isYesValue(
    readNestedPath(applicant, 'applicant.visa_history.previously_applied'),
  );

  // Dependent child of the relationship with the sponsor: number_of_children > 0
  // OR any migrating family member flagged as a child of this relationship.
  const numChildren = Number(readNestedPath(applicant, 'applicant.relationship.number_of_children'));
  const migrating = readNestedPath(applicant, 'applicant.migrating_family.entries');
  const childOfRelationship =
    Array.isArray(migrating) &&
    migrating.some((e) => isYesValue((e as Record<string, unknown> | null)?.is_child_of_relationship) === true);
  if ((Number.isFinite(numChildren) && numChildren > 0) || childOfRelationship) {
    out.dependentChildWithSponsor = true;
  }

  // Applicant + sponsor prior relationships (repeaters store entries as arrays).
  out.applicantHadPriorRelationship = isYesValue(
    readNestedPath(applicant, 'applicant.previous_relationships.has_previous'),
  );
  applyPriorRelationship(out, 'applicant', readNestedPath(applicant, 'applicant.previous_relationships.entries'));
  out.sponsorHadPriorRelationship = isYesValue(
    readNestedPath(sponsor, 'sponsor.previous_relationships.has_previous'),
  );
  applyPriorRelationship(out, 'sponsor', readNestedPath(sponsor, 'sponsor.previous_relationships.entries'));

  // 600 tourist: has the applicant declared an Australia-based inviter/supporter?
  // Drives the inviter document set (documentChecklist.ts). application.* fields
  // live on the applicant's own 1419 intake form, so read from the applicant blob.
  //
  // SIGNAL POINT 1 OF 2 (Wave 5). The second is `hasInviterParty` on
  // checklistForVisa's options, set when an Inviter party is linked on the
  // matter (matters.sponsor_contact_id). Either is sufficient to surface the
  // inviter documents, so staff adding an inviter at matter creation no longer
  // have to wait for the applicant to reach the funding question. Keep the two
  // in step: change one and read the other. `application.has_supporters` is
  // deliberately NOT renamed (item 32 reworks it separately).
  out.hasInviter = isYesValue(readNestedPath(applicant, 'application.has_supporters'));

  // 600 tourist item 28: a self-employed applicant must supply proof of business
  // registration. Sourced from the split employment_status option.
  out.applicantSelfEmployed =
    readNestedPath(applicant, 'applicant.employment_status') === 'Self employed';

  // Relationship-evidence tabs (partner 47SP forms, Jane's reorg 2026-07-16).
  // Structured situation answers that flip the matching evidence documents to
  // required. Untouched tabs leave every signal undefined (docs stay optional).
  out.jointBankAccount = isYesValue(readNestedPath(applicant, 'applicant.evidence.financial.joint_bank_account'));
  out.jointAssets = isYesValue(readNestedPath(applicant, 'applicant.evidence.financial.joint_assets'));
  out.regularMoneyTransfers = isYesValue(readNestedPath(applicant, 'applicant.evidence.financial.regular_money_transfers'));
  out.jointRentalOrOwnership = isYesValue(readNestedPath(applicant, 'applicant.evidence.household.joint_lease_or_ownership'));
  out.livingTogether = isYesValue(readNestedPath(applicant, 'applicant.evidence.household.living_together'));
  out.jointTravel = isYesValue(readNestedPath(applicant, 'applicant.evidence.social.joint_travel'));
  out.jointInvitations = isYesValue(readNestedPath(applicant, 'applicant.evidence.social.joint_invitations'));
  out.socialMediaVisible = isYesValue(readNestedPath(applicant, 'applicant.evidence.social.social_media'));
  const will = isYesValue(readNestedPath(applicant, 'applicant.evidence.commitment.will_beneficiary'));
  const superIns = isYesValue(readNestedPath(applicant, 'applicant.evidence.commitment.super_insurance_beneficiary'));
  if (will !== undefined || superIns !== undefined) {
    out.willsOrBeneficiaries = will === true || superIns === true;
  }

  return out;
}

/** Coerce a stored value into a tri-state boolean (Yes/No/true/false). */
function isYesValue(v: unknown): boolean | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'yes' || s === 'true') return true;
    if (s === 'no' || s === 'false') return false;
  }
  return undefined;
}

/**
 * Read a prior-relationships repeater's first entry and set the side's
 * prior-relationship type + deceased flag on the answers object. The schema's
 * relationship_type enum (relationship-status.json) covers Married / Divorced /
 * De facto / Separated / Never married / Widowed.
 */
function applyPriorRelationship(
  out: IntakeAnswers,
  side: 'applicant' | 'sponsor',
  entries: unknown,
): void {
  if (!Array.isArray(entries) || entries.length === 0) return;
  const type = (entries[0] as Record<string, unknown> | null)?.relationship_type;
  if (typeof type !== 'string') return;
  const s = type.toLowerCase();
  let norm: IntakeAnswers['applicantPriorRelationshipType'];
  let deceased = false;
  if (s.includes('widow')) deceased = true; // prior partner died -> death certificate
  else if (s.includes('divorc') || s.includes('marri')) norm = 'married'; // divorce document
  else if (s.includes('de facto') || s.includes('defacto') || s.includes('de-facto')) norm = 'defacto';
  else if (s.includes('separat')) norm = 'separated';
  else if (s.includes('never') || s.includes('single')) norm = 'never-married';
  if (side === 'applicant') {
    out.applicantPriorRelationshipType = norm;
    out.applicantPriorPartnerDeceased = deceased || undefined;
  } else {
    out.sponsorPriorRelationshipType = norm;
    out.sponsorPriorPartnerDeceased = deceased || undefined;
  }
}
